import { useMemo, useRef, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { api, type Artikel, type Lieferant, type SammelzettelPos } from '../lib/api';
import { banfCsv, fmtDatum, fmtMenge, STATUS_TXT } from '../lib/banfFormat';
import { NeuEtikett } from '../components/EinkaufNeu';

// Positionstabelle der BANF im Stil SAP ME21N/ME51N: Auswahl + Sammelaktionen, Inline-Bearbeitung,
// Status-Filter mit Zählern, Suche, Sortierung, Gruppierung nach Lieferant, Detail-Panel je Position, CSV-Export.

type Filter = 'alle' | 'offen' | 'chef' | 'bestellt' | 'geliefert' | 'storniert';
type SortKey = 'pos' | 'menge' | 'bezeichnung' | 'lieferant';

const istWartend = (p: SammelzettelPos) => p.status === 'offen' && p.rueckfrage === 'wartet';
const istOffenFrei = (p: SammelzettelPos) => p.status === 'offen' && p.rueckfrage !== 'wartet';
const passt = (p: SammelzettelPos, f: Filter) =>
  f === 'alle' ? true : f === 'offen' ? istOffenFrei(p) : f === 'chef' ? istWartend(p) : p.status === f;

export function BanfPositionen({ neuIds, zettelId, dv, pos, nummern, aktiv, darfEntscheiden, lieferanten, onReload, onFehler, onFrage, onAblehnen, onBestellen }: {
  neuIds?: Set<number>;
  zettelId: number; dv: string; pos: SammelzettelPos[]; nummern: Record<number, number>; aktiv: boolean; darfEntscheiden: boolean;
  lieferanten: Lieferant[]; onReload: () => void; onFehler: (m: string) => void;
  onFrage: (ps: SammelzettelPos[]) => void; onAblehnen: (p: SammelzettelPos) => void; onBestellen: (ps: SammelzettelPos[]) => void;
}) {
  const [filter, setFilter] = useState<Filter>('alle');
  const [zeigeStorno, setZeigeStorno] = useState(false);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'pos', dir: 1 });
  const [gruppiert, setGruppiert] = useState(false);
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [detail, setDetail] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const zaehl = (f: Filter) => pos.filter(p => passt(p, f)).length;
  const sichtbar = useMemo(() => {
    let l = pos.filter(p => passt(p, filter));
    if (filter === 'alle' && !zeigeStorno) l = l.filter(p => p.status !== 'storniert' || p.rueckfrage === 'abgelehnt');
    const s = q.trim().toLowerCase();
    if (s) l = l.filter(p => [p.bezeichnung, p.lieferant, p.lieferanten_artikelnr, p.bemerkung].some(x => (x ?? '').toLowerCase().includes(s)));
    const cmp = (a: SammelzettelPos, b: SammelzettelPos): number => {
      if (sort.key === 'menge') return (a.menge - b.menge) * sort.dir;
      if (sort.key === 'bezeichnung') return a.bezeichnung.localeCompare(b.bezeichnung, 'de') * sort.dir;
      if (sort.key === 'lieferant') return (a.lieferant ?? '').localeCompare(b.lieferant ?? '', 'de') * sort.dir;
      return ((nummern[a.id] ?? 0) - (nummern[b.id] ?? 0)) * sort.dir;
    };
    l = [...l].sort(cmp);
    if (gruppiert) l = [...l].sort((a, b) => (a.lieferant ?? '~').localeCompare(b.lieferant ?? '~', 'de'));
    return l;
  }, [pos, filter, zeigeStorno, q, sort, gruppiert, nummern]);

  const waehlbar = sichtbar.filter(p => aktiv && istOffenFrei(p));
  const gewaehlt = pos.filter(p => sel.has(p.id) && istOffenFrei(p));
  const alleGewaehlt = waehlbar.length > 0 && waehlbar.every(p => sel.has(p.id));
  const toggle = (id: number) => setSel(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const kopfSort = (key: SortKey) => setSort(s => s.key === key ? { key, dir: (s.dir * -1) as 1 | -1 } : { key, dir: 1 });
  const pfeil = (key: SortKey) => sort.key === key ? (sort.dir === 1 ? ' ▲' : ' ▼') : '';

  async function lauf(fn: () => Promise<unknown>) {
    setBusy(true); onFehler('');
    try { await fn(); onReload(); } catch (e) { onFehler((e as Error).message); }
    setBusy(false);
  }
  const speichern = (p: SammelzettelPos, feld: string, wert: string) => lauf(() => api.sammelzettel.updatePos(p.id, { [feld]: wert }));
  const posAktion = (p: SammelzettelPos, status: SammelzettelPos['status']) => {
    if (status === 'offen' && !confirm('Bestellung dieser Position zurücksetzen?')) return;
    return lauf(() => api.sammelzettel.updatePos(p.id, { status }));
  };
  const duplizieren = (p: SammelzettelPos) => lauf(() => api.sammelzettel.addPos(zettelId, {
    menge: p.menge, einheit: p.einheit ?? '', bezeichnung: p.bezeichnung, artikel_id: p.artikel_id,
    lieferant: p.lieferant ?? '', lieferanten_artikelnr: p.lieferanten_artikelnr ?? '', bemerkung: p.bemerkung ?? '',
  }));
  const massenStorno = () => {
    if (!gewaehlt.length || !confirm(`${gewaehlt.length} Position${gewaehlt.length === 1 ? '' : 'en'} stornieren?`)) return;
    return lauf(async () => { for (const p of gewaehlt) await api.sammelzettel.updatePos(p.id, { status: 'storniert' }); setSel(new Set()); });
  };
  function csv() {
    const blob = new Blob([banfCsv(sichtbar, nummern)], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = `BANF-${String(zettelId).padStart(4, '0')}_${dv.replace(/[^\w.-]+/g, '_')}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }

  const chips: [Filter, string][] = [['alle', 'Alle'], ['offen', 'Offen'], ['chef', 'Beim Chef'], ['bestellt', 'Bestellt'], ['geliefert', 'Wareneingang'], ['storniert', 'Storniert']];
  const spalten = 10;
  let letzteGruppe: string | null = null;

  return (
    <div className="mb-4">
      {/* Werkzeugleiste: Status-Filter mit Zählern, Suche, Gruppierung, Export */}
      <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="pos-toolbar">
        <div className="flex flex-wrap gap-1">
          {chips.map(([k, l]) => {
            const n = k === 'alle' ? pos.filter(p => p.status !== 'storniert').length : zaehl(k);
            if (k !== 'alle' && n === 0) return null;
            return (
              <button key={k} data-testid={`chip-${k}`} onClick={() => setFilter(k)}
                      className={`px-2.5 py-1 rounded-full text-xs border ${filter === k ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}>
                {l} ({n})
              </button>
            );
          })}
        </div>
        <input className="input w-52 py-1 text-sm" placeholder="🔍 in Positionen suchen" aria-label="Positionen filtern" value={q} onChange={e => setQ(e.target.value)} />
        <label className="text-xs text-slate-600 flex items-center gap-1.5">
          <input type="checkbox" checked={gruppiert} onChange={e => setGruppiert(e.target.checked)} /> nach Lieferant gruppieren
        </label>
        {zaehl('storniert') > 0 && filter === 'alle' && (
          <label className="text-xs text-slate-600 flex items-center gap-1.5">
            <input type="checkbox" checked={zeigeStorno} onChange={e => setZeigeStorno(e.target.checked)} /> stornierte zeigen
          </label>
        )}
        <button className="btn-secondary ml-auto text-xs py-1" aria-label="Positionen als CSV exportieren" onClick={csv}>⬇ CSV</button>
      </div>

      {/* Sammelaktionen für die Auswahl */}
      {gewaehlt.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm" data-testid="pos-aktionsleiste">
          <b>{gewaehlt.length} Position{gewaehlt.length === 1 ? '' : 'en'} ausgewählt</b>
          <button disabled={busy} className="bg-brand text-white rounded-lg px-3 py-1 text-xs font-semibold disabled:opacity-50" data-testid="auswahl-bestellen" onClick={() => onBestellen(gewaehlt)}>📦 Auswahl bestellen</button>
          <button disabled={busy} className="btn-secondary text-xs py-1" data-testid="auswahl-chef" onClick={() => onFrage(gewaehlt)}>⏳ Beim Chef nachfragen</button>
          <button disabled={busy} className="btn-secondary text-xs py-1 text-red-600" data-testid="auswahl-storno" onClick={massenStorno}>Stornieren</button>
          <button className="text-xs text-slate-500 underline ml-auto" onClick={() => setSel(new Set())}>Auswahl aufheben</button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-100 border-b border-slate-300 text-xs text-slate-600 uppercase">
            <tr>
              <th className="px-3 py-2 w-8">
                {aktiv && <input type="checkbox" aria-label="Alle wählbaren Positionen markieren" checked={alleGewaehlt} disabled={waehlbar.length === 0}
                                 onChange={e => setSel(e.target.checked ? new Set(waehlbar.map(p => p.id)) : new Set())} />}
              </th>
              {([['pos', 'Pos.'], ['menge', 'Menge'], [null, 'ME'], ['bezeichnung', 'Kurztext'], [null, 'Lieferanten-Art.-Nr.'], ['lieferant', 'Lieferant'], [null, 'Bemerkung'], [null, 'Stand'], [null, '']] as [SortKey | null, string][]).map(([k, h], i) => (
                <th key={i} className={`text-left px-3 py-2 font-medium whitespace-nowrap ${k ? 'cursor-pointer select-none hover:text-slate-900' : ''}`}
                    onClick={k ? () => kopfSort(k) : undefined} aria-sort={k && sort.key === k ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}>
                  {h}{k ? pfeil(k) : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="zettel-positionen">
            {sichtbar.length === 0 && (
              <tr><td colSpan={spalten} className="text-center py-8 text-slate-500">
                {pos.length === 0 ? 'Noch keine Positionen — unten eine Position anlegen.' : 'Keine Position für diesen Filter.'}
              </td></tr>
            )}
            {sichtbar.map(p => {
              const kopf = gruppiert && (p.lieferant ?? '') !== letzteGruppe;
              if (gruppiert) letzteGruppe = p.lieferant ?? '';
              const frei = aktiv && istOffenFrei(p);
              const nr = nummern[p.id] ?? '';
              const edit = aktiv && p.status !== 'storniert';
              return (
                <GruppeUndZeile key={p.id} kopf={kopf ? `${p.lieferant?.trim() || 'ohne Lieferant'} · ${sichtbar.filter(x => (x.lieferant ?? '') === (p.lieferant ?? '')).length} Position(en)` : null} spalten={spalten}>
                  <tr className={`${p.status === 'storniert' ? 'opacity-50' : ''} ${detail === p.id ? 'bg-sky-50' : ''}`}>
                    <td className="px-3 py-2">
                      {frei && <input type="checkbox" aria-label={`Position ${p.id} markieren`} checked={sel.has(p.id)} onChange={() => toggle(p.id)} />}
                    </td>
                    <td className="px-3 py-2 text-slate-400" data-testid={`posnr-${p.id}`}>{nr}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <Zelle wert={fmtMenge(p.menge)} anzeige={fmtMenge(p.menge)} editierbar={frei} label={`Position ${p.id} Menge`} onSave={v => speichern(p, 'menge', v)} schmal />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <Zelle wert={p.einheit ?? ''} anzeige={p.einheit ?? ''} editierbar={frei} label={`Position ${p.id} ME`} onSave={v => speichern(p, 'einheit', v)} schmal />
                    </td>
                    <td className="px-3 py-2 font-medium">
                      <Zelle wert={p.bezeichnung} anzeige={<>{neuIds?.has(p.id) && <span className="mr-1.5"><NeuEtikett klein /></span>}{p.bezeichnung}{p.bedarf_id ? <span className="ml-1 text-xs text-slate-400">(aus Bedarf #{p.bedarf_id})</span> : null}</>}
                             editierbar={frei} label={`Position ${p.id} Kurztext`} onSave={v => speichern(p, 'bezeichnung', v)} />
                    </td>
                    <td className="px-3 py-2">
                      <Zelle wert={p.lieferanten_artikelnr ?? ''} anzeige={p.lieferanten_artikelnr ?? '—'} editierbar={frei} label={`Position ${p.id} Art.-Nr.`} onSave={v => speichern(p, 'lieferanten_artikelnr', v)} />
                    </td>
                    <td className="px-3 py-2">
                      <Zelle wert={p.lieferant ?? ''} anzeige={p.lieferant ?? '—'} editierbar={frei} label={`Position ${p.id} Lieferant`} list="banf-lieferanten" onSave={v => speichern(p, 'lieferant', v)} />
                    </td>
                    <td className="px-3 py-2">
                      <Zelle wert={p.bemerkung ?? ''} anzeige={p.bemerkung ?? ''} editierbar={edit} label={`Position ${p.id} Bemerkung`} onSave={v => speichern(p, 'bemerkung', v)} />
                    </td>
                    <td className={`px-3 py-2 whitespace-nowrap ${STATUS_TXT[p.status].c}`}>
                      {STATUS_TXT[p.status].t}
                      {p.status !== 'offen' && p.status !== 'storniert' && p.bestell_datum ? <div className="text-xs text-slate-500">{fmtDatum(p.bestell_datum)} · {p.besteller ?? ''} · {p.bestellnr ?? ''}</div> : null}
                      {p.status === 'offen' && p.rueckfrage === 'wartet' && (
                        <div className="text-xs text-amber-800 whitespace-normal max-w-[260px]" data-testid={`frage-${p.id}`}>
                          ⏳ <b>beim Chef</b> · „{p.frage_text}“<div className="text-slate-500">{p.frage_von ?? ''} · {fmtDatum(p.frage_am ?? null)}</div>
                        </div>
                      )}
                      {p.status === 'offen' && p.rueckfrage === 'freigegeben' && (
                        <div className="text-xs text-emerald-700 whitespace-normal max-w-[260px]" data-testid={`frei-${p.id}`}>
                          ✓ freigegeben von {p.entscheid_von ?? '—'} · {fmtDatum(p.entscheid_am ?? null)}{p.entscheid_text ? ` — „${p.entscheid_text}“` : ''}
                        </div>
                      )}
                      {p.status === 'storniert' && p.rueckfrage === 'abgelehnt' && (
                        <div className="text-xs text-red-700 whitespace-normal max-w-[260px] no-underline" data-testid={`abgelehnt-${p.id}`}>
                          abgelehnt von {p.entscheid_von ?? '—'}: „{p.entscheid_text}“
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button className="text-slate-400 hover:text-sky-700 mr-2" aria-label={`Details Position ${p.id}`} title="Details zur Position"
                              onClick={() => setDetail(d => d === p.id ? null : p.id)}>ⓘ</button>
                      {aktiv && p.status !== 'storniert' && (
                        <button className="text-slate-400 hover:text-slate-700 mr-2" aria-label={`Position ${p.id} duplizieren`} title="Position duplizieren" disabled={busy}
                                onClick={() => duplizieren(p)}>⧉</button>
                      )}
                      {aktiv && istOffenFrei(p) && (
                        <button className="text-amber-700 hover:underline mr-3" aria-label={`Position ${p.id} beim Chef nachfragen`}
                                title="Position zurückstellen und beim Chef nachfragen" onClick={() => onFrage([p])}>Chef fragen</button>
                      )}
                      {aktiv && istWartend(p) && (darfEntscheiden ? <>
                        <button className="text-emerald-700 font-semibold hover:underline mr-3" aria-label={`Position ${p.id} freigeben`} onClick={() => lauf(() => api.sammelzettel.entscheid(p.id, 'freigeben'))}>✓ Freigeben</button>
                        <button className="text-red-600 hover:underline mr-3" aria-label={`Position ${p.id} ablehnen`} onClick={() => onAblehnen(p)}>Ablehnen</button>
                      </> : (
                        <button className="text-slate-500 hover:underline mr-3" aria-label={`Position ${p.id} Frage zurückziehen`} onClick={() => lauf(() => api.sammelzettel.frageZurueck(p.id))}>↩ Frage zurückziehen</button>
                      ))}
                      {aktiv && istOffenFrei(p) && <button className="text-slate-400 hover:text-red-600" aria-label={`Position ${p.id} entfernen`} title="Position stornieren"
                                                             onClick={() => posAktion(p, 'storniert')}>✕</button>}
                      {aktiv && p.status === 'bestellt' && <>
                        <button className="text-emerald-700 hover:underline mr-3" aria-label={`Position ${p.id} geliefert`} onClick={() => posAktion(p, 'geliefert')}>Wareneingang ✓</button>
                        <button className="text-slate-500 hover:underline" aria-label={`Position ${p.id} zurücksetzen`} onClick={() => posAktion(p, 'offen')}>↩ zurücksetzen</button>
                      </>}
                    </td>
                  </tr>
                  {detail === p.id && (
                    <tr><td colSpan={spalten} className="bg-sky-50/60 px-4 py-3"><PosDetail p={p} nr={nr} onClose={() => setDetail(null)} /></td></tr>
                  )}
                </GruppeUndZeile>
              );
            })}
          </tbody>
        </table>
      </div>
      <datalist id="banf-lieferanten">{lieferanten.map(l => <option key={l.id} value={l.name} />)}</datalist>
    </div>
  );
}

// Gruppenkopf (optional) + Zeilen — Fragment mit Key
function GruppeUndZeile({ kopf, spalten, children }: { kopf: string | null; spalten: number; children: React.ReactNode }) {
  return <>{kopf && <tr data-testid="gruppen-kopf"><td colSpan={spalten} className="bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">{kopf}</td></tr>}{children}</>;
}

// Zelle mit Klick-zum-Bearbeiten (Enter/Verlassen speichert, Esc verwirft)
function Zelle({ wert, anzeige, editierbar, label, onSave, list, schmal }: {
  wert: string; anzeige: React.ReactNode; editierbar: boolean; label: string; onSave: (v: string) => void; list?: string; schmal?: boolean;
}) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(wert);
  const abbruch = useRef(false);
  if (!editierbar) return <>{anzeige}</>;
  if (!edit) {
    return (
      <span role="button" tabIndex={0} aria-label={`${label} bearbeiten`} title="Klicken zum Bearbeiten"
            className="block min-h-[1.25rem] cursor-text rounded px-1 -mx-1 hover:bg-amber-50"
            onClick={() => { abbruch.current = false; setV(wert); setEdit(true); }}
            onKeyDown={e => { if (e.key === 'Enter') { abbruch.current = false; setV(wert); setEdit(true); } }}>
        {anzeige === '' || anzeige == null ? <span className="text-slate-300">—</span> : anzeige}
      </span>
    );
  }
  return (
    <input autoFocus aria-label={label} list={list} className={`input py-1 text-[13px] ${schmal ? 'w-16' : 'w-full min-w-[9rem]'}`} value={v}
           onChange={e => setV(e.target.value)}
           onKeyDown={e => {
             if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
             if (e.key === 'Escape') { abbruch.current = true; setEdit(false); }
           }}
           onBlur={() => {
             if (abbruch.current) { abbruch.current = false; return; }
             setEdit(false);
             if (v.trim() !== wert.trim()) onSave(v.trim());
           }} />
  );
}

// Detail-Panel (SAP-Positionsdetails): Artikelstamm · Bestellung · Rückfrage/Freigabe · Bedarf
function PosDetail({ p, nr, onClose }: { p: SammelzettelPos; nr: number | string; onClose: () => void }) {
  const stamm = useApi<Artikel | null>(async () => {
    if (!p.artikel_id) return null;
    try {
      const r = await api.artikel.list({ q: p.lieferanten_artikelnr || p.bezeichnung, aktiv: 'alle', limit: 20 });
      return r.data.find(a => a.id === p.artikel_id) ?? null;
    } catch { return null; }
  }, [p.id, p.artikel_id]);
  const a = stamm.data;
  const Feld = ({ l, children }: { l: string; children: React.ReactNode }) => (
    <div><div className="text-[11px] uppercase tracking-wide text-slate-400">{l}</div><div className="text-slate-800">{children || '—'}</div></div>
  );
  return (
    <div data-testid="pos-detail" className="text-[13px]">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-slate-800">Position {nr} · Details</div>
        <button className="text-xs text-slate-500 underline" onClick={onClose}>schließen</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
        <Feld l="Artikelstamm">{p.artikel_id ? (stamm.loading ? 'lädt …' : a ? 'verknüpft' : 'verknüpft (nicht gefunden)') : 'nicht im Stamm'}</Feld>
        <Feld l="Warengruppe">{a?.warengruppe}</Feld>
        <Feld l="Bisher bestellt">{a?.zugriffe != null ? `${a.zugriffe}× (Menge ${a.gesamtmenge ?? '—'})` : ''}</Feld>
        <Feld l="Statistik-Zeitraum">{a?.stat_von ? `${fmtDatum(a.stat_von)} – ${fmtDatum(a.stat_bis ?? null)}` : ''}</Feld>
        <Feld l="Lieferant">{p.lieferant}</Feld>
        <Feld l="Lieferanten-Art.-Nr.">{p.lieferanten_artikelnr}</Feld>
        <Feld l="Bestellung">{p.bestellnr ? `${p.bestellnr} · ${fmtDatum(p.bestell_datum)} · ${p.besteller ?? ''}` : ''}</Feld>
        <Feld l="Liefertermin">{p.liefertermin}</Feld>
        <Feld l="Rückfrage">{p.frage_text ? `„${p.frage_text}“ (${p.frage_von ?? '—'}, ${fmtDatum(p.frage_am ?? null)})` : ''}</Feld>
        <Feld l="Entscheidung">{p.entscheid_von ? `${p.rueckfrage === 'abgelehnt' ? 'abgelehnt' : 'freigegeben'} von ${p.entscheid_von}, ${fmtDatum(p.entscheid_am ?? null)}${p.entscheid_text ? ` — „${p.entscheid_text}“` : ''}` : ''}</Feld>
        <Feld l="Blech-Bedarf">{p.bedarf_id ? `#${p.bedarf_id}` : ''}</Feld>
        <Feld l="Bemerkung">{p.bemerkung}</Feld>
      </div>
    </div>
  );
}
